import { useNavigate } from "react-router";
import { GraduationCap, School, Users, ShieldCheck, ArrowRight } from "lucide-react";
import config from "../../../config";

const ALL_PORTALS = [
  {
    key: "student",
    icon: GraduationCap,
    title: "Students",
    subtitle: "Discover your career path and unlock your potential",
    accent: "from-blue-600 to-indigo-600",
    bg: "from-blue-50 to-indigo-50",
    signinPath: "/signin",
    signupPath: "/signup",
    signupLabel: "Create Account",
    devOnly: false,
  },
  {
    key: "school",
    icon: School,
    title: "Schools",
    subtitle: "Manage students, track progress and drive outcomes",
    accent: "from-emerald-500 to-teal-600",
    bg: "from-emerald-50 to-teal-50",
    signinPath: "/school/signin",
    signupPath: "/school/signup",
    signupLabel: "Register School",
    devOnly: false,
  },
  {
    key: "parent",
    icon: Users,
    title: "Parents & Guardians",
    subtitle: "Stay informed and support your child's learning journey",
    accent: "from-amber-500 to-orange-500",
    bg: "from-amber-50 to-orange-50",
    signinPath: "/parent/signin",
    signupPath: null,
    devOnly: false,
  },
  {
    key: "admin",
    icon: ShieldCheck,
    title: "Super Admin",
    subtitle: "Platform administration and oversight",
    accent: "from-violet-600 to-purple-700",
    bg: "from-violet-50 to-purple-50",
    signinPath: "/admin/login",
    signupPath: null,
    devOnly: true,
  },
];

const isProduction = config.appMode === "production";

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <main className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100 flex flex-col items-center justify-center p-6">
      {/* Decorative blobs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-16 left-8 w-80 h-80 bg-blue-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-24 right-8 w-96 h-96 bg-indigo-200/20 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-5xl space-y-10">
        {/* Header */}
        <div className="text-center space-y-3">
          <div className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-sm border border-white/70 rounded-full px-4 py-1.5 shadow-sm mb-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs font-semibold text-slate-600 tracking-wide">EarlyScape Platform</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tight">
            Welcome back
          </h1>
          <p className="text-slate-500 text-lg max-w-md mx-auto">
            Choose your portal to continue
          </p>
        </div>

        {/* Portal cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {ALL_PORTALS.filter((p) => !(p.devOnly && isProduction)).map((portal) => {
            const Icon = portal.icon;
            return (
              <div
                key={portal.key}
                className={`relative bg-gradient-to-br ${portal.bg} border border-white/80 rounded-3xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col gap-5`}
              >
                {/* Icon */}
                <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${portal.accent} flex items-center justify-center shadow-md`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>

                {/* Text */}
                <div className="space-y-1 flex-1">
                  <h2 className="text-xl font-bold text-slate-900">{portal.title}</h2>
                  <p className="text-sm text-slate-500 leading-relaxed">{portal.subtitle}</p>
                </div>

                {/* Actions */}
                <div className="space-y-2.5">
                  <button
                    onClick={() => navigate(portal.signinPath)}
                    className={`w-full py-2.5 px-4 rounded-xl bg-gradient-to-r ${portal.accent} text-white text-sm font-semibold flex items-center justify-center gap-2 shadow-sm hover:opacity-90 transition-opacity`}
                  >
                    Sign In
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  {portal.signupPath && (
                    <button
                      onClick={() => navigate(portal.signupPath)}
                      className="w-full py-2.5 px-4 rounded-xl bg-white/70 hover:bg-white/90 border border-white/80 text-slate-700 text-sm font-semibold transition-colors"
                    >
                      {portal.signupLabel}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-slate-400">
          &copy; {new Date().getFullYear()} EarlyScape. All rights reserved.
        </p>
      </div>
    </main>
  );
}
